import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/ui/password-input';
import { Shield, AlertCircle, Lock, Mail, ArrowRight, ArrowLeft, ShieldCheck, Eye, Server, Loader2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import AuthLayout from '../components/AuthLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const navigate = useNavigate();
  const { setAuthData } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password,
        portal: 'admin',
      });
      const { access_token, user } = response.data;

      // Double sécurité côté client : seuls super_admin et admin sont acceptés
      if (user.role !== 'super_admin' && user.role !== 'admin') {
        setError('Accès réservé aux administrateurs uniquement. Redirection…');
        setLoading(false);
        setTimeout(() => navigate('/login'), 1500);
        return;
      }

      setAuthData(access_token, user);
      const back = sessionStorage.getItem('redirect_after_login');
      sessionStorage.removeItem('redirect_after_login');
      navigate(back && back.startsWith('/admin') ? back : (user.role === 'super_admin' ? '/admin/super-dashboard' : '/admin'));
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 401) setError('Email ou mot de passe incorrect. Veuillez vérifier vos identifiants.');
      else if (status === 403) {
        if (detail?.includes('console membres') || detail?.toLowerCase().includes('membres')) {
          setError(detail + ' Redirection en cours…');
          setTimeout(() => navigate('/login'), 1500);
        } else if (detail?.includes('désactivé')) setError('Votre compte a été désactivé. Veuillez contacter le support.');
        else if (detail?.includes('attente')) setError('Votre compte est en attente de validation.');
        else setError(detail || 'Accès refusé.');
      } else if (status === 429) setError('Trop de tentatives. Veuillez réessayer dans quelques minutes.');
      else if (status === 500) setError('Erreur serveur. Veuillez réessayer plus tard.');
      else if (!err.response) setError('Impossible de se connecter au serveur. Vérifiez votre connexion internet.');
      else setError(detail || 'Une erreur est survenue lors de la connexion.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      variant="admin"
      badge="Zone d'administration"
      badgeIcon={Shield}
      title="Console"
      titleHighlight="Super Admin"
      description="Espace réservé aux administrateurs habilités de la plateforme COPPAM. Toute connexion est tracée et auditée conformément à la politique de sécurité."
      features={[
        { icon: ShieldCheck, label: 'Authentification forte requise' },
        { icon: Eye, label: 'Toutes les actions sont auditées' },
        { icon: Server, label: 'Accès aux 200+ collections de données' },
      ]}
    >
      <div className="mb-2">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à la connexion standard
        </Link>
      </div>

      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mt-3 mb-2">
        Connexion administrateur
      </h2>
      <p className="text-slate-600 dark:text-slate-300 text-sm mb-7">
        Authentifiez-vous avec votre compte Super Admin pour accéder au panel de contrôle global.
      </p>

      {error && (
        <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
        <div>
          <Label htmlFor="admin-email" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Email administrateur
          </Label>
          <div className="relative mt-1.5">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              className="pl-10 h-12"
              required
              autoComplete="email"
              data-testid="admin-email-input"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="admin-password" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Mot de passe
          </Label>
          <div className="relative mt-1.5">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
            <PasswordInput
              id="admin-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="pl-10 h-12"
              required
              autoComplete="current-password"
              data-testid="admin-password-input"
            />
          </div>
        </div>

        <div className="flex items-center justify-end text-sm">
          <Link
            to="/reset-password/admin"
            className="text-red-600 dark:text-red-400 hover:underline font-medium"
          >
            Mot de passe oublié ?
          </Link>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full h-12 text-base font-semibold bg-gradient-to-r from-red-600 to-rose-700 hover:from-red-700 hover:to-rose-800 text-white shadow-lg shadow-red-900/30 hover:shadow-xl group"
          data-testid="admin-login-submit-btn"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <ShieldCheck className="w-5 h-5 mr-2" />
          )}
          {loading ? 'Authentification…' : 'Accéder à la console'}
          {!loading && <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-0.5 transition-transform" />}
        </Button>
      </form>

      <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 rounded-lg p-3">
          <Shield className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <span>
            Cet espace est strictement réservé. Toute tentative d&apos;accès non autorisée est
            tracée et peut faire l&apos;objet de poursuites.
          </span>
        </div>
      </div>
    </AuthLayout>
  );
};

export default AdminLogin;
